import type { ReactNode } from "react";
import type { To } from "react-router";
import starsVideo from "../../../assets/videos/stars.mp4";
import IconButton from "../../primitives/components/IconButton/IconButton";
import type { IconName } from "../../primitives/icons";
import cx from "../../../lib/cx.js";
import { styles } from "./pageShell.css";

/**
 * A button the shell places for the page: leading the header's title, or —
 * for a page with no header row — pinned in the sheet's corner. Given `to` it
 * renders as a link (`replace` swapping the current history entry instead of
 * pushing one); `onClick` is for what a link can't express, like the history's
 * back.
 */
type ShellButton = {
  icon: IconName;
  ariaLabel: string;
  /** Anchors the button for callers that need to point at it (the user tour). */
  id?: string;
} & (
  | { to: To; replace?: boolean; onClick?: never }
  | { onClick: () => void; to?: never; replace?: never }
);

/**
 * How the page is headed. The default is the header row above the sheet: a
 * title slot, optionally led by a button. A page whose content is the whole
 * point — one showroom entry filling the sheet — drops the row entirely and
 * takes `cornerButton` instead: a single button pinned inside the sheet's
 * top-left corner, over the body. The two are exclusive; a page that has a
 * header row already has somewhere to put its button.
 */
type HeaderProps =
  | { titleSlot: ReactNode; titleButton?: ShellButton; cornerButton?: never }
  | { cornerButton: ShellButton; titleSlot?: never; titleButton?: never };

/**
 * How the sheet gets filled. The default is `children` in PageShell's own body:
 * inset from the sheet's edges, scrolling as one. A page whose content has to
 * reach those edges — a list whose scrollbar belongs on the border — takes the
 * body over with `bodySlot` instead, and insets its content itself. `scrollBody`
 * has nothing to say about a body the page owns, so the two shapes are
 * exclusive.
 */
type BodyProps =
  | {
      children: ReactNode;
      /**
       * Whether the body scrolls its own overflow. Pass `false` when the content
       * manages its own scrolling (e.g. independently scrolling columns) so the
       * body clips instead of scrolling as a whole. Defaults to `true`.
       */
      scrollBody?: boolean;
      bodySlot?: never;
    }
  | {
      bodySlot: ReactNode;
      children?: never;
      scrollBody?: never;
    };

interface BaseProps {
  /**
   * Pinned below the sheet, wizard navigation,
   * progress, and anything else that must not scroll with the body.
   */
  bottomSlot?: ReactNode;
  /**
   * Which theme the sheet pins. `"light"` (the default) gives the white sheet;
   * `"dark"` puts the body on black, behind the starfield.
   *
   * `"legacy-genially-bg-light"` is a bandaid: a light sheet on the legacy
   * Genially background (`#F9F9F9`), so embedded geniallies' own white reads
   * against it. Remove once genially is gone.
   */
  surface?: "light" | "dark" | "legacy-genially-bg-light";
  /**
   * Whether a dark surface plays its starfield video. Defaults to `true`;
   * pass `false` for a dark sheet whose backdrop is supplied elsewhere.
   */
  showBackdrop?: boolean;
  /**
   * Whether a dark surface outlines its sheet. Defaults to `true`; pass
   * `false` for a dark sheet with no backdrop to hold an edge against.
   */
  bordered?: boolean;
}

type PageShellProps = BaseProps & HeaderProps & BodyProps;

const ShellIconButton = ({
  button,
  variant,
}: {
  button: ShellButton;
  variant: "secondary" | "tertiary";
}) => {
  const { id, icon, ariaLabel } = button;
  const shared = {
    id,
    icon,
    ariaLabel,
    size: "large",
    color: "neutral",
    variant,
  } as const;

  return button.to !== undefined ? (
    <IconButton {...shared} to={button.to} replace={button.replace} />
  ) : (
    <IconButton {...shared} onClick={button.onClick} />
  );
};

const PageShell = ({
  titleSlot,
  titleButton,
  cornerButton,
  bottomSlot,
  surface = "light",
  showBackdrop = true,
  bordered = true,
  scrollBody = true,
  children,
  bodySlot,
}: PageShellProps) => {
  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {titleSlot && (
          <header className={styles.header}>
            {titleButton && (
              <ShellIconButton button={titleButton} variant="secondary" />
            )}
            <div className={styles.titleSlot}>{titleSlot}</div>
          </header>
        )}

        <div className={styles.sheet({ surface, bordered })}>
          {surface === "dark" && showBackdrop && (
            <video
              aria-hidden
              className={styles.backdrop}
              src={starsVideo}
              autoPlay
              muted
              loop
              playsInline
            />
          )}
          {/* Before the body in the DOM, so the tab order reaches it first;
              `corner` lifts it over the body it floats on. */}
          {cornerButton && (
            <div className={styles.corner}>
              <ShellIconButton button={cornerButton} variant="tertiary" />
            </div>
          )}
          {bodySlot ? (
            <div className={styles.slotBody}>{bodySlot}</div>
          ) : (
            <div
              className={cx(
                styles.body(),
                cornerButton && styles.belowCorner,
                scrollBody ? styles.scroll : styles.noScroll,
              )}
            >
              {children}
            </div>
          )}
        </div>

        {bottomSlot && <div className={styles.bottom}>{bottomSlot}</div>}
      </div>
    </div>
  );
};

export default PageShell;
